import { useCallback } from "react";

import { useAppState } from "../../contexts/App";

/**
 * The Keepo's address: /sites/<short id>/<screen>. The short id is the first characters of its uuid: among the
 * person's own Keepos (the only ones the editor opens) it's enough to tell which one, and it never changes.
 */
const SHORT_ID_LENGTH = 8;

export const shortSiteId = (siteId: string) => siteId.slice(0, SHORT_ID_LENGTH);

/** The Keepo's screen ("" is the Keepo itself, "/settings", "/editor/sections"...). */
export const sitePath = (siteId: string, path = "") => `/sites/${shortSiteId(siteId)}${path}`;

/** The screens that used to live at the root (/keepo, /settings...): their address in the Keepo. */
export const LEGACY_SITE_ROUTES = ["/keepo", "/editor", "/seo", "/analytics", "/contacts", "/settings"];

export const fromLegacyPath = (siteId: string, legacyPath: string) =>
  sitePath(siteId, legacyPath.replace(/^\/keepo(?=\/|$)/, ""));

const SITE_PATH = /^\/sites\/[^/]+(\/.*)?$/;
// the screens that are the Keepo's own, under /keepo
const KEEPO_SCREENS = /^\/(plus|pages)(\/|$)/;

/**
 * A Keepo's address as the old screen it is (/sites/<id> → /keepo, /sites/<id>/plus → /keepo/plus,
 * /sites/<id>/pages → /keepo/pages, /sites/<id>/settings → /settings): what the layout decides by. Any other address stays as it is.
 */
export const toLegacyPath = (pathname: string) => {
  const match = pathname.match(SITE_PATH);
  if (!match) {
    return pathname;
  }
  const screen = (match[1] ?? "").replace(/\/+$/, "");
  return !screen ? "/keepo" : KEEPO_SCREENS.test(screen) ? `/keepo${screen}` : screen;
};

/** The Keepo of an address: the whole id, or the short one when only one of the person's Keepos starts with it. */
export const findKeepo = <T extends { siteId: string }>(keepos: T[], id: string): T | undefined => {
  const exact = keepos.find(({ siteId }) => siteId === id);
  if (exact || id.length < SHORT_ID_LENGTH) {
    return exact;
  }
  const starting = keepos.filter(({ siteId }) => siteId.startsWith(id));
  return starting.length === 1 ? starting[0] : undefined;
};

/** The address of a screen of the Keepo being edited. */
export const useSitePath = () => {
  const { appState } = useAppState();
  const siteId = appState.editingSiteId ?? "";
  return useCallback((path = "") => sitePath(siteId, path), [siteId]);
};
